import { CdkScrollable } from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatError, MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';

import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { UserService } from 'src/app/services/user.service';

import { editorConfig } from '../../models/editorconfig';
import { EditorComponent } from '../editor/editor.component';

@Component({
  selector: 'app-settle-dialog',
  templateUrl: './settle-dialog.component.html',
  styleUrls: ['./settle-dialog.component.scss'],
  imports: [
    EditorComponent,
    MatDialogTitle,
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    CKEditorModule,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatButton,
    MatDialogClose
  ],
  providers: [DatePipe]
})
export class SettleDialogComponent implements OnInit, OnDestroy {
  data = inject(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<SettleDialogComponent>>(MatDialogRef);
  snackBar = inject(MatSnackBar);
  private _adapter = inject<DateAdapter<Date>>(DateAdapter);
  userService = inject(UserService);

  editor = ClassicEditor;

  form!: UntypedFormGroup;
  dialogForm!: UntypedFormGroup;
  displayName!: string;
  datePipe = inject(DatePipe);
  now = new Date();
  radio!: string;

  editorConfig = {};

  ngOnInit() {
    this.displayName = this.userService.userProfile().displayName ?? '';
    this.initializeForm();
    this.buildCustomFormGroup();
    this.dateFormat();
    this.editorConfig = editorConfig.standard;
  }

  ngOnDestroy() {
    this.dialogForm.get('shortText2')?.clearValidators();
    this.dialogForm.controls['shortText2'].setErrors(null);
    this.dialogForm.get('prolongationDeadline')?.clearValidators();
    this.dialogForm.controls['prolongationDeadline'].setErrors(null);
  }

  buildCustomFormGroup() {
    // Use getRawValue() to get values even if controls are disabled
    const formRawValue = this.form?.getRawValue?.() || this.form?.value || {};
    const commentExecutionValue = formRawValue.commentExecution || '';
    const amountCapexValue = formRawValue.amountCapex || '';

    this.dialogForm = new UntypedFormGroup({
      amountCapex: new UntypedFormControl(
        {
          value: amountCapexValue,
          disabled: false
        },
        [Validators.pattern('^[0-9]*$')]
      ),
      commentExecution: new UntypedFormControl(
        {
          value: commentExecutionValue,
          disabled: false
        },
        [Validators.required]
      ),
      prolongationDeadline: new UntypedFormControl(
        {
          value: this.now,
          disabled: false
        },
        [Validators.required]
      ),
      shortText2: new UntypedFormControl(
        {
          value: '',
          disabled: false
        },
        [Validators.required]
      )
    });
  }

  dateFormat() {
    this._adapter.setLocale('de');
  }

  initializeForm() {
    this.form = this.data.formGroup;
  }

  checkProlongationDeadline(): boolean {
    const today = this.dialogForm.controls['prolongationDeadline'].value > this.now;
    const init =
      this.dialogForm.controls['prolongationDeadline'].value > new Date(this.form.controls['initialDeadline'].value);
    return today && init;
  }

  checkAction(action: string) {
    return this.radio === action;
  }

  checkArea(area: string) {
    return this.form?.get('auditor')?.value === area;
  }

  isProlongationSubmitted() {
    return this.form?.get('ask_for_prolongation')?.value !== 1;
  }

  isSettledSubmitted() {
    return this.form?.get('status')?.value !== 'Settled';
  }

  settleRecommendation() {
    this.dialogForm.get('shortText2')?.clearValidators();
    this.dialogForm.controls['shortText2'].setErrors(null);
    this.dialogForm.get('prolongationDeadline')?.clearValidators();
    this.dialogForm.controls['prolongationDeadline'].setErrors(null);

    if (this.dialogForm.valid) {
      this.form.controls['status'].setValue('Settled');
      this.form.controls['settledBy'].setValue(this.userService.userProfile().id);
      this.form.controls['amountCapex'].setValue(parseInt(this.dialogForm.value.amountCapex, 10));
      this.form.controls['commentExecution'].setValue(this.dialogForm.value.commentExecution);
      this.snackBar.open("The status is now settled. Don't forget to save!", undefined, {
        duration: 6000
      });
      this.dialogRef.close();
    } else {
      this.dialogForm.markAllAsTouched();
    }
  }

  askForProlongation() {
    this.dialogForm.controls['shortText2'].markAsTouched();
    this.dialogForm.get('amountCapex')?.clearValidators();
    this.dialogForm.controls['amountCapex'].setErrors(null);
    this.dialogForm.get('commentExecution')?.clearValidators();
    this.dialogForm.controls['commentExecution'].setErrors(null);
    if (this.checkProlongationDeadline()) {
      this.dialogForm.controls['prolongationDeadline'].setErrors(null);
      this.form.controls['shortText2'].setValue(this.dialogForm.value.shortText2);
      this.form.controls['prolongationDeadline'].setValue(this.dialogForm.value.prolongationDeadline);
    } else {
      this.dialogForm.controls['prolongationDeadline'].setErrors({
        incorrect: true
      });
      this.dialogForm.controls['prolongationDeadline'].markAsTouched();
    }
    if (this.dialogForm.valid) {
      this.form.controls['prolongationStatus'].setValue('Asked');
      this.form.controls['prolongationGrantedBy'].setValue('');
      this.form.controls['prolongationGrantedDate'].setValue('');
      this.form.controls['prolongationDecision'].setValue('2');
      this.form.controls['prolongation_request_date'].setValue('');
      this.form.controls['ask_for_prolongation'].setValue(true);
      this.form.addControl('current_prolongationStatus', new UntypedFormControl('Asked'));
      this.displayName = this.userService.userProfile().displayName ?? '';
      this.form.controls['prolongationRequestBy'].setValue(this.displayName);
      this.dialogRef.close();
    } else {
      this.form.markAllAsTouched();
    }
  }
}
